import React, { useState, useEffect } from 'react';
import type { Page } from '../types.ts';
import { NAVIGATION_ITEMS, NavigationItem } from '../constants.tsx';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import TrialCountdownWidget from './onboarding/TrialCountdownWidget';

interface SidebarProps {
  activePage: Page;
  setActivePage: (page: Page) => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;

  restricted?: boolean;
  currentUser?: any;
  onLogout?: () => void;
}

export default function Sidebar({
  activePage,
  setActivePage,
  isOpen,
  setIsOpen,
  restricted,
  currentUser,
  onLogout
}: SidebarProps): JSX.Element {
  const [openSubmenus, setOpenSubmenus] = useState<string[]>([]);
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    const activeParent = NAVIGATION_ITEMS.find(item =>
      item.children?.some(child => child.page === activePage)
    );
    if (activeParent && !openSubmenus.includes(activeParent.name) && !isCollapsed) {
      setOpenSubmenus(prev => [...prev, activeParent.name]);
    }
  }, [activePage, openSubmenus, isCollapsed]);

  const handleParentClick = (item: NavigationItem) => {
    if (isCollapsed) {
      setIsCollapsed(false);
      // Automatically open this submenu after expanding
      setTimeout(() => {
        if (item.children) {
          setOpenSubmenus(prev => [...prev, item.name]);
        }
      }, 50);
      return;
    }

    if (item.children) {
      setOpenSubmenus(prev =>
        prev.includes(item.name)
          ? prev.filter(n => n !== item.name)
          : [...prev, item.name]
      );
    }
    if (item.page) {
      setActivePage(item.page);
    }
  };

  const handleNavigationClick = (page: Page) => {
    setActivePage(page);
    setIsOpen(false); // Mobile: close sidebar
  }

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
    if (!isCollapsed) {
      setOpenSubmenus([]); // Close open submenus when collapsing
    }
  };

  return (
    <>
      {/* Overlay for mobile */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden transition-opacity ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setIsOpen(false)}
      ></div>

      <aside className={`fixed inset-y-0 left-0 ${isCollapsed ? 'w-20' : 'w-64'} flex-shrink-0 bg-surface border-r border-border flex flex-col z-30 transform transition-all duration-300 ease-in-out lg:relative lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className={`h-16 flex items-center ${isCollapsed ? 'justify-center' : 'justify-between px-4'} border-b border-border flex-shrink-0`}>
          {isCollapsed ? (
            <img src="/cherry-logo.png" alt="Cherry CRM" className="dashboard-logo-icon" />
          ) : (
            <img src="/cherry-logo-full.png" alt="Cherry CRM" className="dashboard-logo" />
          )}
          {!isCollapsed && (
            <button onClick={toggleCollapse} className="text-text-subtle hover:text-primary transition-colors focus:outline-none">
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
          )}
        </div>

        <nav className="flex-1 px-3 py-6 space-y-2 overflow-y-auto overflow-x-hidden">
          {NAVIGATION_ITEMS.filter(item => {
            if (restricted) return ['Dashboard', 'Settings', 'Support'].includes(item.name);

            // Access Control: Restricted Admin Modules
            if ((item.name === 'Team Management' || item.name === 'Billing') && currentUser?.role !== 'client_admin') {
              return false;
            }

            return true;
          }).map((item) => {
            const isParentActive = item.children?.some(child => child.page === activePage);
            const isSubmenuOpen = openSubmenus.includes(item.name);
            const isManagementSectionStart = item.name === 'Team Management' && currentUser?.role === 'client_admin';

            if (item.children) {
              return (
                <div key={item.name}>
                  {isManagementSectionStart && !isCollapsed && (
                    <div className="px-3 mt-6 mb-2 text-xs font-semibold text-text-subtle uppercase tracking-wider">
                      Management
                    </div>
                  )}
                  {isManagementSectionStart && isCollapsed && (
                    <div className="my-2 border-t border-border"></div>
                  )}
                  <button
                    onClick={() => handleParentClick(item)}
                    className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} px-3 py-2.5 text-sm font-medium rounded-lg transition-colors duration-200 group ${isParentActive
                      ? 'bg-primary/10 text-primary font-semibold'
                      : 'text-text hover:bg-gray-100 hover:text-primary'
                      }`}
                    title={isCollapsed ? item.name : undefined}
                  >
                    <div className="flex items-center">
                      <item.icon className={`h-6 w-6 flex-shrink-0 ${!isCollapsed ? 'mr-3' : ''}`} />
                      {!isCollapsed && <span>{item.name}</span>}
                    </div>
                    {!isCollapsed && (
                      <ChevronDownIcon className={`h-4 w-4 transition-transform ${isSubmenuOpen ? 'rotate-180' : ''}`} />
                    )}
                  </button>
                  {!isCollapsed && isSubmenuOpen && (
                    <div className="pl-6 mt-1 space-y-1">
                      {item.children.map(child => (
                        <a
                          key={child.page}
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handleNavigationClick(child.page);
                          }}
                          className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${activePage === child.page
                            ? 'bg-primary/10 text-primary font-semibold'
                            : 'text-text hover:bg-gray-100 hover:text-primary'
                            }`}
                        >
                          <span className="w-1.5 h-1.5 mr-3 bg-current rounded-full"></span>
                          {child.name}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              );
            }



            return (
              <div key={item.name}>
                {isManagementSectionStart && !isCollapsed && (
                  <div className="px-3 mt-6 mb-2 text-xs font-semibold text-text-subtle uppercase tracking-wider">
                    Management
                  </div>
                )}
                {isManagementSectionStart && isCollapsed && (
                  <div className="my-2 border-t border-border"></div>
                )}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (item.page) {
                      handleNavigationClick(item.page);
                    }
                  }}
                  className={`flex items-center ${isCollapsed ? 'justify-center' : ''} px-3 py-2.5 text-sm font-medium rounded-lg transition-colors duration-200 ${activePage === item.page
                    ? 'bg-primary/10 text-primary font-semibold'
                    : 'text-text hover:bg-gray-100 hover:text-primary'
                    }`}
                  title={isCollapsed ? item.name : undefined}
                >
                  <item.icon className={`h-6 w-6 flex-shrink-0 ${!isCollapsed ? 'mr-3' : ''}`} />
                  {!isCollapsed && <span>{item.name}</span>}
                </a>
              </div>
            );
          })}
        </nav>

        <div className={`p-4 border-t border-border flex flex-col gap-4 ${isCollapsed ? 'items-center' : ''}`}>
          {!isCollapsed && (
            <div className="flex justify-between items-center w-full">
              <span className="text-xs text-text-subtle">v1.0.0</span>
              <button onClick={toggleCollapse} className="p-1 rounded-lg hover:bg-gray-100 text-text-subtle hover:text-primary transition-colors">
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
            </div>
          )}
          {isCollapsed && (
            <button onClick={toggleCollapse} className="p-2 rounded-lg hover:bg-gray-100 text-text-subtle hover:text-primary transition-colors">
              <ChevronRightIcon className="h-6 w-6" />
            </button>
          )}

          {/* Trial Countdown Widget */}
          {!isCollapsed && currentUser?.subscription_status === 'trialing' && (
            <div className="mb-4">
              <TrialCountdownWidget
                daysRemaining={7}
                status="trialing"
              />
            </div>
          )}

          {/* Sign out removed to avoid redundancy with Header navigation */}
        </div>
      </aside >
    </>
  );
}
